// Utility functions for handling speaker grouping and ordering

export interface Speaker {
  id: string | number;
  name: string;
  title?: string;
  company?: string;
  image?: string;
  type?: string;
  position?: number;
  role?: string; // Some components use role instead of type
  [key: string]: any; // Allow for additional properties
}

export interface SpeakerGroup {
  type: string;
  speakers: Speaker[];
  minPosition: number; // Lowest position in the group for ordering groups
}

/**
 * Groups speakers by their type/role and maintains order within groups
 * Groups are ordered by the lowest position number in each group
 */
export function groupSpeakersByType(speakers: Speaker[]): SpeakerGroup[] {
  if (!speakers || speakers.length === 0) return [];

  // First, ensure all speakers have a position
  const speakersWithPosition = speakers.map((speaker, index) => ({
    ...speaker,
    position: speaker.position ?? index + 1,
    type: speaker.type || speaker.role || 'Speaker'
  }));

  // Group speakers by type
  const groups = speakersWithPosition.reduce((acc, speaker) => {
    const type = speaker.type || 'Speaker';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(speaker);
    return acc;
  }, {} as Record<string, Speaker[]>);

  // Convert to array and calculate min position for each group
  const groupArray: SpeakerGroup[] = Object.entries(groups).map(([type, speakers]) => {
    // Sort speakers within the group by position
    const sortedSpeakers = [...speakers].sort((a, b) => (a.position || 0) - (b.position || 0));
    const minPosition = Math.min(...sortedSpeakers.map(s => s.position || 0));

    return {
      type,
      speakers: sortedSpeakers,
      minPosition
    };
  });

  // Sort groups by their minimum position
  return groupArray.sort((a, b) => a.minPosition - b.minPosition);
}

/**
 * Get a display label for a speaker type
 */
export function getSpeakerTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    // Handle both lowercase and capitalized versions
    'speaker': 'Speakers',
    'Speaker': 'Speakers',
    'presenter': 'Presenters',
    'Presenter': 'Presenters',
    'host': 'Hosts',
    'Host': 'Hosts',
    'moderator': 'Moderators',
    'Moderator': 'Moderators',
    'panelist': 'Panelists',
    'Panelist': 'Panelists',
    'judge': 'Judges',
    'Judge': 'Judges',
    'keynote': 'Keynote Speakers',
    'Keynote': 'Keynote Speakers',
    'sponsor': 'Sponsor Representatives',
    'Sponsor': 'Sponsor Representatives'
  };

  // Try exact match first, then lowercase
  return typeLabels[type] || typeLabels[type.toLowerCase()] || `${type}s`;
}

/**
 * Get a badge style for a speaker type
 */
export function getSpeakerTypeBadgeStyle(type: string): string {
  const styles: Record<string, string> = {
    'moderator': 'bg-purple-50 text-purple-700 border-purple-200',
    'panelist': 'bg-blue-50 text-blue-700 border-blue-200',
    'keynote': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'presenter': 'bg-green-50 text-green-700 border-green-200',
    'host': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    'judge': 'bg-red-50 text-red-700 border-red-200',
    'sponsor': 'bg-gray-50 text-gray-700 border-gray-200'
  };

  const lowerType = type.toLowerCase();
  return styles[lowerType] || 'bg-gray-50 text-gray-700 border-gray-200';
}

/**
 * Sort speakers by position, handling undefined positions
 */
export function sortSpeakersByPosition(speakers: Speaker[]): Speaker[] {
  return [...speakers].sort((a, b) => {
    const posA = a.position ?? 999;
    const posB = b.position ?? 999;
    return posA - posB;
  });
}